import { ssrExchange, SSRExchange, SSRExchangeParams, SSRData } from '@urql/next'

export const ssrDataStore = new Map<number, SSRData>()

/**
 * Creates an enhanced SSR exchange that captures and preserves SSR data by chainId.
 *
 * @description
 * This function solves a specific problem with URQL's SSR functionality when using multiple clients.
 *
 * By default, URQL's ssrExchange clears its data after the first restore/use because it assumes
 * the cacheExchange will handle subsequent requests. This works fine for a single client, but
 * causes issues when:
 * 1. Creating new clients when users log in/out
 * 2. Switching between different networks/chains
 * 3. Any scenario requiring multiple URQL client instances
 *
 * This wrapper intercepts the `restoreData` call and stores a copy of all SSR data by chainId
 * in a global store (ssrDataStore). This allows us to:
 * - Maintain SSR data beyond the first client instance
 * - Share SSR data between multiple clients on the same chain
 * - Preserve the user experience when changing authentication state or networks
 *
 * @param chainId - The blockchain network ID to associate the SSR data with
 * @param params - Optional configuration parameters for the base SSR exchange
 * @returns An enhanced SSR exchange that preserves data by chainId
 */
export const createSpyingSSRExchange = (chainId: number, params: SSRExchangeParams = {}): SSRExchange => {
  const baseSsr = ssrExchange(params)

  const originalRestoreData = baseSsr.restoreData

  baseSsr.restoreData = (data: SSRData) => {
    originalRestoreData(data)
    ssrDataStore.set(chainId, { ...ssrDataStore.get(chainId), ...data })
  }

  return baseSsr
}
